import { type RefObject, useMemo, useRef } from 'react';
import { bigQuery } from '@fiverr-private/obs';
import { getContext } from '@fiverr-private/fiverr_context';
import { useAnalyticAdapter } from '../analyticAdapter';
import { getPlatform } from '../../utils';
import type { BIMeta } from '../types';

export const useVideoBigQueryEvents = (videoRef: RefObject<HTMLVideoElement>, meta: BIMeta) => {
    const BIMeta = useRef(meta);

    const callbacks = useMemo(() => {
        const { locale, userId } = getContext();

        const sendEvent = (props) => {
            const { videoName, pageName } = BIMeta.current;

            const base = {
                group: 'user_action',
                platform: getPlatform(),
                page: {
                    name: pageName,
                    element: {
                        name: videoName,
                    },
                },
                user: {
                    id: userId,
                    locale,
                },
            };

            return bigQuery.send({
                ...base,
                ...props,
            });
        };

        const onPlay = () => {
            const { autoPlay, subtitleLocale } = BIMeta.current;

            sendEvent({
                type: 'video_play',
                video: {
                    is_auto_play: autoPlay,
                    subtitles_language: subtitleLocale,
                },
            });
        };

        const onPause = () => {
            const { autoPlay, subtitleLocale } = BIMeta.current;

            sendEvent({
                type: 'video_pause',
                video: {
                    is_auto_play: autoPlay,
                    subtitles_language: subtitleLocale,
                },
            });
        };
        const onWatched = () => {
            const { subtitleLocale } = BIMeta.current;
            const { duration, currentTime } = videoRef.current || {};

            sendEvent({
                type: 'video_finished_watching',
                video: {
                    is_completed: currentTime === duration,
                    watch_duration: currentTime,
                    subtitles_language: subtitleLocale,
                },
            });
        };

        const onMute = () => {
            const { subtitleLocale } = BIMeta.current;
            const { currentTime } = videoRef.current || {};

            sendEvent({
                type: 'video_mute',
                video: {
                    subtitles_language: subtitleLocale,
                    watch_duration: currentTime,
                },
            });
        };

        const onUnmute = () => {
            const { subtitleLocale } = BIMeta.current;
            const { currentTime } = videoRef.current || {};

            sendEvent({
                type: 'video_unmute',
                video: {
                    subtitles_language: subtitleLocale,
                    watch_duration: currentTime,
                },
            });
        };

        const onSubtitleChange = (incomeLocale) => {
            const { subtitleLocale } = BIMeta.current;

            sendEvent({
                type: 'video_changed_subtitles_language',
                video: {
                    subtitles_language: incomeLocale,
                    subtitles_previous_language: subtitleLocale,
                },
            });
        };

        return {
            onPlay,
            onPause,
            onWatched,
            onMute,
            onUnmute,
            onSubtitleChange,
        };
    }, [videoRef, BIMeta]);

    useAnalyticAdapter(videoRef, callbacks);
};
